import React, { useCallback, useEffect, useState } from 'react';

import { Constants } from '@/src/utils';
import { useAuth } from '@/src/app/context/useAuth';
import { Container, Loading } from '@/src/ui';
import { apiRequest } from '@/src/services';
import StudentHistoryFilters from './components/StudentHistoryFilters';
import StudentHistoryTable from './components/StudentHistoryTable';
import { type StudentHistoryDTO } from './models/studentHistoryDTOts';
import type { FilterState } from './models/filterDTOs';

const StudentHistoryPage: React.FC = () => {
    document.title = 'Student History';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<Array<StudentHistoryDTO>>([]);
    const [hasSearched, setHasSearched] = useState(false);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const mode = user?.role === Constants.ROLES.STUDENT ? 'student-limited' : 'normal';

    const [filterState, setFilterState] = useState<FilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    examId:
                        parsed.examId ||
                        (parsed.examIds && parsed.examIds.length > 0 ? parsed.examIds[0] : 0),
                    studentId: 0,
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            examId: 0,
            studentId: 0,
        };
    });

    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                examId: filterState.examId,
                examIds: [filterState.examId],
            };
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, UNIVERSAL_FILTER_KEY]);

    // Auto-fetch info for student users, since filter rows will be hidden
    const [hasAutoFetchedStudent, setHasAutoFetchedStudent] = useState(false);
    useEffect(() => {
        let firstAttemptTimestamp: number | null = null;
        const studentLoadInterval = setInterval(() => {
            if (!firstAttemptTimestamp) {
                firstAttemptTimestamp = Date.now();
            } else if (mode === 'normal' || hasAutoFetchedStudent) {
                clearInterval(studentLoadInterval);
            } else if (
                Date.now() - (firstAttemptTimestamp ?? 0) > 10000 || // Give up after 10 seconds
                (filterState.districtId !== 0 &&
                    filterState.schoolId !== 0 &&
                    filterState.gradeLevelMasterId !== 0)
            ) {
                setHasAutoFetchedStudent(true);
                refreshTableData(filterState);
            }
        }, 250);
        return () => clearInterval(studentLoadInterval);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState, hasAutoFetchedStudent, mode]);

    const refreshTableData = useCallback(
        async ({
            gradeLevelMasterId,
            districtId,
            schoolId,
            classId,
            examId,
            studentId,
        }: FilterState = filterState) => {
            setError('');
            setLoaded(false);
            setHasSearched(true);

            // **Once there is more data in the DB, remove "/test" to use regular endpoint
            let requestUrl = `reports/studentHistoryReport?`;
            requestUrl += `districtId=${districtId}`;
            requestUrl += `&schoolId=${schoolId}`;
            requestUrl += `&gradeLevelMasterId=${gradeLevelMasterId}`;
            if (mode === 'student-limited') {
                requestUrl += `&classId=0`;
                requestUrl += `&examId=0`;
                requestUrl += `&studentId=0`;
            } else {
                requestUrl += `&classId=${classId}`;
                requestUrl += `&examId=${examId}`;
                requestUrl += `&studentId=${studentId}`;
            }

            try {
                const res = await apiRequest<StudentHistoryDTO[]>('GET', requestUrl);
                setData(res.data);
            } catch {
                setError('Failed to load Student History Report');
            } finally {
                setLoaded(true);
            }
        },
        [filterState, mode]
    );

    return (
        <Container>
            <h1 className="page-header">Student History Report</h1>
            <br />

            <StudentHistoryFilters
                refreshTableData={refreshTableData}
                mode={mode}
                filterState={filterState}
                setFilterState={setFilterState}
            />

            {hasSearched && (
                <StudentHistoryTable data={data} isLoading={!loaded} mode={mode} error={error} />
            )}
            {!hasSearched && mode === 'student-limited' && (
                <Loading message="Loading Student History Report..." />
            )}
        </Container>
    );
};

export default StudentHistoryPage;
